import { FC } from 'react';
import { css, keyframes } from '@emotion/react';
import { colors, spacing } from '../styles';
import BloomLogo from '../assets/Icons/bloom_logo.svg';

interface SplashScreenProps {
  isOffline?: boolean;
}

const pulseAnimation = keyframes`
  0%, 20% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  80%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
`;

const SplashScreen: FC<SplashScreenProps> = ({ isOffline = false }) => {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={isOffline ? 'No internet connection, waiting for connection' : 'Loading Bloom application'}
      css={css`
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background-color: ${colors.sugar};
        z-index: 9999;
        html[data-theme='dark'] & {
          background-color: ${colors.liquorice};
        }
      `}
    >
      {/* Bloom Logo */}
      <img
        src={BloomLogo}
        alt="Bloom"
        css={css`
          width: 120px;
          height: 120px;
          margin-bottom: ${spacing.kilo};
        `}
      />

      {/* Loading Dots */}
      <div
        role="progressbar"
        aria-label="Loading"
        css={css`
          display: flex;
          gap: ${spacing.milli};
          align-items: center;
          margin-bottom: ${spacing.hecto};
        `}
      >
        <div
          css={css`
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background-color: ${colors.petrolBlue};
            animation: ${pulseAnimation} 1.4s infinite ease-in-out;
            animation-delay: 0s;
            html[data-theme='dark'] & {
              background-color: ${colors.skyBlue};
            }
          `}
        />
        <div
          css={css`
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background-color: ${colors.petrolBlue};
            animation: ${pulseAnimation} 1.4s infinite ease-in-out;
            animation-delay: 0.2s;
            html[data-theme='dark'] & {
              background-color: ${colors.skyBlue};
            }
          `}
        />
        <div
          css={css`
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background-color: ${colors.petrolBlue};
            animation: ${pulseAnimation} 1.4s infinite ease-in-out;
            animation-delay: 0.4s;
            html[data-theme='dark'] & {
              background-color: ${colors.skyBlue};
            }
          `}
        />
      </div>

      {/* Offline Message */}
      {isOffline && (
        <div
          role="alert"
          aria-live="assertive"
          css={css`
            margin-top: ${spacing.kilo};
            color: ${colors.tungsten};
            text-align: center;
            padding: 0 ${spacing.regular};
            html[data-theme='dark'] & {
              color: ${colors.cloud};
            }
          `}
        >
          Waiting for internet connection...
          <br />
          <span
            css={css`
              font-size: 14px;
              opacity: 0.7;
            `}
          >
            Please connect to wifi or cellular to use Bloom
          </span>
        </div>
      )}
    </div>
  );
};

export default SplashScreen;
